import React, { useEffect, useMemo, useState } from "react";
import { message } from "antd";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import CustomLayout from "../../Layout/CustomLayout";
import PharmacySideMenu from "../../Layout/CustomLayout/PharmacySideMenu";
import {
  ListScreen,
  ListHead,
  DistBand,
  ListCard,
  IconAction,
  RowActions,
  RefCell,
  NameCell,
  CellRow,
  TypeTag,
  BedTag,
  StatusPill,
  NumCell,
  DimCell,
  pageSizeOf,
  PHARMACY_PAGE_SIZE,
  ListTable,
} from "../common/ListScreen";
import { getTablePagination } from "../../../utils/tablePagination";
import {
  getPharmacyBillsListApi,
  recordPharmacyPaymentApi,
} from "../../../services/pharmacyBilling.services";
import RecordPaymentModal from "../Orders/RecordPaymentModal";
import "./Billing.css";
import { formatAmount, formatMoney } from "../common/money";
import { UiButton } from "../../ui";
import PharmacyGlobalSearch from "../common/PharmacyGlobalSearch";


const inr0 = (value) =>
  formatMoney(Math.round(Number(value) || 0)).replace(".00", "");

/* A bill with something still owed on it is one you can collect against. */
const COLLECTABLE = ["pending", "partial_payment"];

const STATUS_TONE = {
  pending: ["#FFFAEB", "#B54708"],
  partial_payment: ["#FFFAEB", "#B54708"],
  paid: ["#ECFDF3", "#027A48"],
  cancelled: ["#F2F4F7", "#475467"],
};

const SALE_TYPE_LABEL = {
  IP: "IP",
  OP: "OP",
  PATIENT: "Patient",
  COUNTER: "Counter Sale",
};

/*
 * The sale types, as the band's segments. They were tabs in the page header
 * before; as segments they carry their counts on the same row as the total and
 * leave the header to the search and the account menu.
 */
const SALE_TYPES = [
  { key: "IP", label: "IP", color: "#3538CD" },
  { key: "OP", label: "OP", color: "#12B76A" },
  { key: "PATIENT", label: "Patient", color: "#EAAA5E" },
  { key: "COUNTER", label: "Counter Sale", color: "#9B8AFB" },
];

const prettyStatus = (value) =>
  String(value || "")
    .split("_")
    .join(" ")
    .replace(/^./, (c) => c.toUpperCase());

const Billing = () => {
  const navigate = useNavigate();
  const [saleType, setSaleType] = useState(null);
  const [counts, setCounts] = useState({});
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  /* The bill being collected against, and whether that is in flight. */
  const [collecting, setCollecting] = useState(null);
  const [saving, setSaving] = useState(false);
  const [refresh, setRefresh] = useState(false);

  useEffect(() => {
    setLoading(true);
    const queryParams = { page, limit: PHARMACY_PAGE_SIZE };
    if (saleType) queryParams.sale_type = saleType;
    if (search) queryParams.search = search;

    getPharmacyBillsListApi(new URLSearchParams(queryParams))
      .then((res) => {
        setData(res?.data);

        if (!search) {
          const perType = res?.data?.sale_type_counts;
          setCounts((prev) =>
            perType
              ? { ...prev, ...perType, ALL: res?.data?.count ?? 0 }
              : { ...prev, [saleType || "ALL"]: res?.data?.count ?? 0 }
          );
        }
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [page, search, saleType, refresh]);

  /*
   * Collecting happens here rather than on the bill page.
   *
   * The dialog is the same one Sale Entry takes money in, so the mode, the
   * reference and the "others" case behave identically wherever the counter
   * collects - and it doubles as the confirmation, since it states what is owed
   * and what is being taken before anything is written.
   */
  const collect = async (payment) => {
    if (!collecting?.id || !payment) return;
    setSaving(true);
    try {
      /* Same rule the bill page settles by: clearing the balance is a
         settlement, anything short of it is a payment against the bill. */
      const due = Number(collecting.due_amount) || 0;
      const settles = Number(payment.amount) >= due - 0.009;

      await recordPharmacyPaymentApi(collecting.id, {
        ...payment,
        payment_type: settles ? "settlement" : "advance",
      });
      message.success(
        `${formatAmount(payment.amount)} collected against ${collecting.bill_number || "this bill"}`
      );
      setCollecting(null);
      setRefresh((prev) => !prev);
    } catch (err) {
      const data = err?.response?.data;
      message.error(
        data?.message || data?.amount?.[0] || data?.non_field_errors?.[0] ||
          "Failed to record the payment"
      );
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: "Bill No",
      dataIndex: "bill_number",
      key: "bill_number",
      render: (value) => <RefCell>{value}</RefCell>,
    },
    {
      title: "Patient",
      key: "patient_name",
      render: (_, record) => (
        <NameCell
          name={record.patient_name || record.counter_sale_customer_name}
          tag={record.patient_mrn}
        />
      ),
    },
    {
      title: "Type",
      key: "sale_type",
      render: (_, record) => (
        <CellRow>
          <TypeTag type={record.sale_type}>
            {SALE_TYPE_LABEL[record.sale_type] || record.sale_type || "-"}
          </TypeTag>
          {record.bed_no ? <BedTag>{record.bed_no}</BedTag> : null}
        </CellRow>
      ),
    },
    {
      title: "Amount",
      dataIndex: "grand_total",
      key: "grand_total",
      align: "right",
      render: (value) => (
        <NumCell>
          <b>{formatAmount(value)}</b>
        </NumCell>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (value) => (
        <StatusPill tone={STATUS_TONE[value] || STATUS_TONE.cancelled}>
          {prettyStatus(value)}
        </StatusPill>
      ),
    },
    {
      title: "Date",
      dataIndex: "created_at",
      key: "created_at",
      render: (value) => (
        <DimCell>{value ? dayjs(value).format("DD MMM YYYY") : "-"}</DimCell>
      ),
    },
    {
      title: "Action",
      key: "action",
      align: "right",
      /*
       * The actions stay against the right edge while the rest of the table
       * scrolls under them. A wide list pushed this column off the end, where
       * its heading read as a cut-off word and its marks could not be reached
       * without scrolling to find them.
       */
      fixed: "right",
      width: 128,
      render: (_, record) => (
        <RowActions>
          <IconAction
            icon="eye"
            label="View bill"
            onClick={() => navigate(`/pharmacy/billing/${record.id}`)}
          />
          {/* Offered only where there is something left to take. */}
          {COLLECTABLE.includes(record.status) && Number(record.due_amount) > 0 ? (
            <UiButton size="sm" onClick={() => setCollecting(record)}>
              Collect
            </UiButton>
          ) : null}
        </RowActions>
      ),
    },
  ];

  const total = Number(data?.count) || 0;

  const segments = SALE_TYPES.map((t) => ({ ...t, count: counts[t.key] ?? 0 }));

  /*
   * What has been billed, and what is still owed on it.
   *
   * Both are sums over every bill, so they are read off the page in hand and
   * only when that page is the whole list - a page of 25 out of 99 would print
   * a quarter of the money as the total.
   */
  const money = useMemo(() => {
    const rows = data?.results || [];
    const count = Number(data?.count) || 0;
    if (!rows.length || rows.length < count || saleType || search) return null;

    return {
      billed: rows.reduce((sum, r) => sum + (Number(r.grand_total) || 0), 0),
      outstanding: rows.reduce((sum, r) => sum + (Number(r.due_amount) || 0), 0),
    };
  }, [data, saleType, search]);
  const bandTotal =
    counts.ALL ?? segments.reduce((sum, s) => sum + s.count, 0);

  return (
    <CustomLayout
      headerSearch={<PharmacyGlobalSearch />}
      layoutClassName="reception-layout pharmacy-motion"
      sideContent={<PharmacySideMenu />}
      header={
        // The mockup's header carries only search and the account menu; each
        // screen names itself in its own heading. Kept truthy so CustomLayout
        // still renders the bar.
        <></>
      }
    >
      <ListScreen>
        <ListHead title="Billing" />

        <DistBand
          total={bandTotal}
          label="Bills"
          segments={segments}
          activeKey={saleType}
          onSelect={(key) => {
            setPage(1);
            // Clicking the type already being shown goes back to all of them.
            setSaleType((prev) => (prev === key ? null : key));
          }}
          meta={money ? `${inr0(money.billed)} billed in all` : undefined}
          aside={
            money
              ? { label: "Outstanding", value: inr0(money.outstanding) }
              : null
          }
        />

        <ListCard
          title="Bills"
          range={{ page, pageSize: pageSizeOf(data, PHARMACY_PAGE_SIZE), total: total }}
          count={total}
          search={{
            value: search,
            onChange: (e) => {
              setSearch(e?.target?.value || "");
              setPage(1);
            },
            placeholder: "Search bill number",
          }}
          onClear={
            search || saleType
              ? () => {
                  setSearch("");
                  setSaleType(null);
                  setPage(1);
                }
              : undefined
          }
        >
          <ListTable
            loading={loading}
            dataSource={data?.results || []}
            columns={columns}
            rowKey="id"
            pagination={getTablePagination({
              current: page,
              pageSize: pageSizeOf(data, PHARMACY_PAGE_SIZE),
              total,
              onChange: (p) => setPage(p),
            })}
          />
        </ListCard>
      <RecordPaymentModal
        open={Boolean(collecting)}
        working={saving}
        title="Collect Payment"
        action="Collect"
        /* A bill is being settled, not raised - there is no sense in
           confirming nothing. */
        requireAmount
        due={Number(collecting?.due_amount) || 0}
        subtitle={[collecting?.patient_name, collecting?.bill_number]
          .filter(Boolean)
          .join(" · ")}
        onCancel={() => (saving ? null : setCollecting(null))}
        onConfirm={collect}
      />

      </ListScreen>
    </CustomLayout>
  );
};

export default Billing;
